import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, MapPin, Mail, Phone, FileText, Link as LinkIcon, Upload, X, CheckCircle2, Clock, Film as FilmIcon, MessageSquare, Check, Trash2, Download, Plus, MoreVertical, Pencil, Sparkles, Loader2, Wrench, Globe } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import api from "@/lib/api";
import { usePricesHidden } from "@/lib/priceVisibility";
import StatusBadge, { STATUS_CONFIG } from "@/components/StatusBadge";
import AdditionalLinksPanel from "@/components/AdditionalLinksPanel";
import ScriptEditor from "@/components/ScriptEditor";
import CommentablePreview from "@/components/CommentablePreview";
import AssetComments from "@/components/AssetComments";
import ItemsEditorModal from "@/components/ItemsEditorModal";
import PhotoGallery from "@/components/PhotoGallery";
import WebsiteSelectionGrid from "@/components/WebsiteSelectionGrid";
import LocationWeatherButton from "@/components/LocationWeatherButton";
import EditRequestDetailsModal from "@/components/EditRequestDetailsModal";
import CoAgentsManager from "@/components/CoAgentsManager";
import AuditLogPanel from "@/components/AuditLogPanel";
import MMApprovalPanel from "@/components/MMApprovalPanel";
import PreviewShareButton from "@/components/PreviewShareButton";
import ProjectShareButton from "@/components/ProjectShareButton";
import AdminAiPhotoLightbox from "@/components/AdminAiPhotoLightbox";
import VideoShotGalleryModal from "@/components/VideoShotGalleryModal";
import AiEditHistoryModal from "@/components/AiEditHistoryModal";
import ZipDownloadOverlay, { useZipDownload, useFolderDownload, FolderDownloadOverlay } from "@/components/ZipDownloadOverlay";
import { buildProjectAssetList, buildAgentPhotoList } from "@/lib/projectAssets";
import { downloadAuthed, downloadStatic } from "@/lib/download";
import { uploadFileChunked } from "@/lib/chunkedUpload";
import { uploadFileDirectToR2 } from "@/lib/directUpload";
import { runWithConcurrency } from "@/lib/concurrency";
import TimePicker from "@/components/TimePicker";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar as Cal } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const fmtDate = (d) => {
    if (!d) return "—";
    const dt = typeof d === "string" ? new Date(d.length <= 10 ? `${d}T00:00:00` : d) : d;
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString("nl-BE", { day: "2-digit", month: "2-digit", year: "numeric" });
};

// Time slots in 15-minute increments from 07:00 → 19:00.
const TIME_SLOTS = (() => {
    const out = [];
    for (let h = 7; h <= 19; h++) {
        for (const m of [0, 15, 30, 45]) {
            if (h === 19 && m > 0) break;
            out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
        }
    }
    return out;
})();

export default function AdminProjectDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [req, setReq] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);

    // status form
    const [newStatus, setNewStatus] = useState("");
    const [adminNotes, setAdminNotes] = useState("");
    const [schedDate, setSchedDate] = useState(null);
    const [schedTime, setSchedTime] = useState("");
    const [savingStatus, setSavingStatus] = useState(false);
    const [overrideMm, setOverrideMm] = useState(false);
    const [editingDescription, setEditingDescription] = useState(false);
    const [descriptionDraft, setDescriptionDraft] = useState("");
    const [savingDescription, setSavingDescription] = useState(false);
    const zipDl = useZipDownload();
    const [pricesHidden] = usePricesHidden();
    const folderDl = useFolderDownload();

    // deliverables form
    const [previewVideoUrl, setPreviewVideoUrl] = useState("");
    const [finalVideoUrl, setFinalVideoUrl] = useState("");
    const [reelPreviewUrl, setReelPreviewUrl] = useState("");
    const [reelUrl, setReelUrl] = useState("");
    // delete confirmation modal
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteInput, setDeleteInput] = useState("");
    const [deleting, setDeleting] = useState(false);
    // items edit modal (optie/formule achteraf toevoegen)
    const [itemsEditOpen, setItemsEditOpen] = useState(false);
    const [aiPhotos, setAiPhotos] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [photoProgress, setPhotoProgress] = useState({ done: 0, total: 0 });
    const [uploadingPreview, setUploadingPreview] = useState(0);
    const [uploadingFinal, setUploadingFinal] = useState(0); // 0-100 progress
    const [uploadingReelPreview, setUploadingReelPreview] = useState(0);
    const [uploadingReel, setUploadingReel] = useState(0);
    const [savingDeliverables, setSavingDeliverables] = useState(false);
    const [comments, setComments] = useState([]);
    const [editDetailsOpen, setEditDetailsOpen] = useState(false);
    // Admin photo lightbox (for Vervangen)
    const [aiLightboxIdx, setAiLightboxIdx] = useState(null);
    const [photoTab, setPhotoTab] = useState("all");
    // Video shots by photo index (admin sees a film badge on photos that already have Kling shots)
    const [videoShotsByPhoto, setVideoShotsByPhoto] = useState({});
    // In-flight video shots per photo (pulses the badge)
    const [activeVideoShotsByPhoto, setActiveVideoShotsByPhoto] = useState({});
    // In-flight AI photo retouches per photo (Nano Banana etc.) — separate pulse
    const [activeAiEditsByPhoto, setActiveAiEditsByPhoto] = useState({});
    // Video gallery modal — shows all completed videoshots for a single photo
    const [videoGalleryIdx, setVideoGalleryIdx] = useState(null);
    // AI-edit history modal — shows all completed AI versions for a single photo
    const [aiHistoryState, setAiHistoryState] = useState({ open: false, photoIdx: null, jobs: [] });

    const load = async () => {
        const [a, b, c, v, e] = await Promise.all([
            api.get(`/requests/${id}`).catch((err) => {
                if (err.response?.status === 404) return { data: null };
                throw err;
            }),
            api.get("/products", { params: { include_hidden: true } }),
            api.get(`/requests/${id}/comments`).catch(() => ({ data: [] })),
            api.get(`/requests/${id}/video-shots`).catch(() => ({ data: [] })),
            api.get(`/requests/${id}/ai-edit-jobs`).catch(() => ({ data: [] })),
        ]);
        if (!a.data) {
            setReq(null);
            setLoading(false);
            return;
        }
        setReq(a.data);
        setProducts(b.data);
        setComments(c.data || []);
        // Aggregate video shots per photo_index. Completed shots whose index no
        // longer points at an existing photo (deleted/replaced photo, or marked
        // -9999 by the repair tool) go into the -1 "orphan" bucket so they stay
        // visible & downloadable instead of silently disappearing.
        const photoCount = (a.data.ai_photo_urls || []).length;
        const vmap = {};
        const aMap = {};
        for (const job of (v.data || [])) {
            let pi = job.photo_index;
            if (typeof pi !== "number") continue;
            if (job.status === "completed") {
                if (pi < 0 || pi >= photoCount) pi = -1;
                if (!vmap[pi]) vmap[pi] = [];
                vmap[pi].push(job);
            } else if (job.status === "pending" || job.status === "processing") {
                aMap[pi] = (aMap[pi] || 0) + 1;
            }
        }
        setVideoShotsByPhoto(vmap);
        setActiveVideoShotsByPhoto(aMap);
        // In-flight AI-edit retouches per photo
        const aiMap = {};
        for (const job of (e.data || [])) {
            const pi = job.photo_index;
            if (typeof pi !== "number") continue;
            if (job.status === "pending" || job.status === "processing") {
                aiMap[pi] = (aiMap[pi] || 0) + 1;
            }
        }
        setActiveAiEditsByPhoto(aiMap);
        setNewStatus(a.data.status);
        setAdminNotes(a.data.admin_notes || "");
        setSchedDate(
            a.data.scheduled_date
                ? new Date(a.data.scheduled_date)
                : (a.data.preferred_date ? new Date(`${a.data.preferred_date}T00:00:00`) : null)
        );
        setSchedTime(a.data.scheduled_time || a.data.preferred_time || "");
        setPreviewVideoUrl(a.data.preview_video_url || "");
        setFinalVideoUrl(a.data.final_video_url || "");
        setReelPreviewUrl(a.data.reel_preview_url || "");
        setReelUrl(a.data.reel_url || "");
        setAiPhotos(a.data.ai_photo_urls || []);
        setLoading(false);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    // Auto-refresh video shots + AI edits every 6s when there are active jobs
    // running so the pulse badges on the photo grid update in real-time as
    // backgrounded Kling / Nano Banana generations complete.
    useEffect(() => {
        if (!id) return;
        const hasActive = Object.keys(activeVideoShotsByPhoto).length > 0
            || Object.keys(activeAiEditsByPhoto).length > 0;
        if (!hasActive) return;
        const refresh = async () => {
            try {
                const [v, e] = await Promise.all([
                    api.get(`/requests/${id}/video-shots`),
                    api.get(`/requests/${id}/ai-edit-jobs`).catch(() => ({ data: [] })),
                ]);
                const vmap = {};
                const aMap = {};
                const photoCount = (req?.ai_photo_urls || []).length;
                for (const job of (v.data || [])) {
                    let pi = job.photo_index;
                    if (typeof pi !== "number") continue;
                    if (job.status === "completed") {
                        if (pi < 0 || pi >= photoCount) pi = -1;
                        if (!vmap[pi]) vmap[pi] = [];
                        vmap[pi].push(job);
                    } else if (job.status === "pending" || job.status === "processing") {
                        aMap[pi] = (aMap[pi] || 0) + 1;
                    }
                }
                setVideoShotsByPhoto(vmap);
                setActiveVideoShotsByPhoto(aMap);
                const aiMap = {};
                for (const job of (e.data || [])) {
                    const pi = job.photo_index;
                    if (typeof pi !== "number") continue;
                    if (job.status === "pending" || job.status === "processing") {
                        aiMap[pi] = (aiMap[pi] || 0) + 1;
                    }
                }
                setActiveAiEditsByPhoto(aiMap);
            } catch { /* non-blocking */ }
        };
        const interval = setInterval(refresh, 6000);
        return () => clearInterval(interval);
    }, [id, activeVideoShotsByPhoto, activeAiEditsByPhoto]);

    // Scroll to top when navigating to a different project
    useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "auto" }); }, [id]);

    // Prevent the browser from opening dropped files when the user drops them outside an upload zone.
    useEffect(() => {
        const stop = (e) => { e.preventDefault(); };
        window.addEventListener("dragover", stop);
        window.addEventListener("drop", stop);
        return () => {
            window.removeEventListener("dragover", stop);
            window.removeEventListener("drop", stop);
        };
    }, []);

    const saveStatus = async () => {
        setSavingStatus(true);
        try {
            await api.patch(`/requests/${id}/status`, {
                status: newStatus,
                admin_notes: adminNotes || null,
                scheduled_date: schedDate ? format(schedDate, "yyyy-MM-dd") : null,
                scheduled_time: schedTime || null,
                override_mm_approval: overrideMm,
            });
            toast.success("Status bijgewerkt");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fout");
        }
        setSavingStatus(false);
    };

    const onUploadPhotos = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        // Reset the input so picking the same files again triggers `onChange`
        // (browsers ignore picking identical files in a row otherwise).
        e.target.value = "";
        setUploading(true);
        const total = files.length;
        setPhotoProgress({ done: 0, total });
        let done = 0;
        const failures = []; // [{name, error}]
        try {
            const results = await runWithConcurrency(files, 3, async (f) => {
                try {
                    const url = await uploadFileDirectToR2(api, f);
                    done++;
                    setPhotoProgress({ done, total });
                    return url;
                } catch (err) {
                    failures.push({ name: f.name, error: err?.message || "onbekend" });
                    return null;
                }
            });
            const urls = results.filter(Boolean);
            if (urls.length > 0) {
                setAiPhotos((prev) => [...prev, ...urls]);
            }
            if (failures.length === 0) {
                toast.success(`${urls.length} foto${urls.length === 1 ? "" : "'s"} geüpload`);
            } else if (urls.length === 0) {
                toast.error(`Upload mislukt — geen enkele foto kon geüpload worden`);
                console.error("Upload failures:", failures);
            } else {
                toast.error(`${urls.length}/${total} foto's geüpload — ${failures.length} mislukt (zie console)`);
                console.error("Upload failures:", failures);
            }
        } catch (err) {
            // runWithConcurrency itself doesn't throw anymore (we swallow per-file
            // errors above), but defensively show something.
            console.error("Bulk upload error:", err);
            toast.error(err?.message || "Upload mislukt");
        }
        setPhotoProgress({ done: 0, total: 0 });
        setUploading(false);
    };

    const uploadVideoFile = async (file, setProgress, setUrl, kind) => {
        if (!file) return;
        setProgress(1);
        try {
            // Chunked upload — bypasses ingress body-size limit (~100MB) so 4K videos of any size go through.
            const data = await uploadFileChunked(api, file, (pct) => setProgress(pct));
            setUrl(data.url);
            toast.success(`${file.name} geüpload (${formatSize(file.size)})`);
            // Auto-persist this single field so the commentable section appears immediately,
            // without forcing the admin to remember to click "Media opslaan".
            try {
                const fieldMap = { preview: "preview_video_url", final: "final_video_url", reel_preview: "reel_preview_url", reel: "reel_url" };
                if (fieldMap[kind]) {
                    await api.patch(`/requests/${id}/deliverables`, { [fieldMap[kind]]: data.url });
                    await load();
                }
            } catch (e) {
                // fail silent; admin can still hit "Media opslaan"
            }
        } catch (e) {
            console.error("Chunked upload failed", e);
            toast.error(e.response?.data?.detail || "Video upload mislukt");
        } finally {
            setProgress(0);
        }
    };

    const removeAiPhoto = (u) => setAiPhotos((prev) => prev.filter((x) => x !== u));

    const saveDeliverables = async () => {
        setSavingDeliverables(true);
        try {
            await api.patch(`/requests/${id}/deliverables`, {
                preview_video_url: previewVideoUrl || null,
                final_video_url: finalVideoUrl || null,
                reel_preview_url: reelPreviewUrl || null,
                reel_url: reelUrl || null,
                ai_photo_urls: aiPhotos,
            });
            toast.success("Media opgeslagen");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Fout");
        }
        setSavingDeliverables(false);
    };

    const startEditDescription = () => {
        setDescriptionDraft(req?.description || "");
        setEditingDescription(true);
    };

    const saveDescription = async () => {
        setSavingDescription(true);
        try {
            const { data } = await api.patch(`/requests/${id}/description`, { description: descriptionDraft });
            setReq(data);
            setEditingDescription(false);
            toast.success("Beschrijving opgeslagen");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Opslaan mislukt");
        }
        setSavingDescription(false);
    };

    if (loading) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center text-zinc-500">Laden...</div>;
    if (!req) return <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center text-zinc-500">Niet gevonden</div>;

    const BACKEND = process.env.REACT_APP_BACKEND_URL;

    const preferredSlotsBlock = (req.preferred_slots && req.preferred_slots.length > 0) ? (
        <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6" data-testid="preferred-slots-block">
            <div className="flex items-center gap-2 mb-4">
                <Calendar className="h-4 w-4 text-[#E5A93D]" />
                <h3 className="font-display font-bold text-lg tracking-tight">Voorkeursmomenten</h3>
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Klik op een voorkeursmoment om in te plannen</div>
            <div className="space-y-2">
                {[{ date: req.preferred_date, time: req.preferred_time }, ...req.preferred_slots].map((s, i) => {
                    const isSelected = schedDate && schedTime &&
                        format(schedDate, "yyyy-MM-dd") === s.date && schedTime === s.time;
                    return (
                        <button
                            key={i}
                            type="button"
                            onClick={() => {
                                setSchedDate(new Date(s.date + "T00:00:00"));
                                setSchedTime(s.time);
                                toast.success(`Optie ${i + 1} gekozen — vergeet niet "Status opslaan" te klikken`);
                            }}
                            className={`w-full text-left flex items-center justify-between gap-3 px-4 py-3 rounded-sm border transition-all active:scale-[0.99] ${isSelected ? "bg-[#E5A93D]/15 border-[#E5A93D]" : "bg-[#F4F4F5] border-zinc-200 hover:border-[#E5A93D]/50 hover:bg-[#F4F4F5]"}`}
                            data-testid={`pick-slot-${i}`}
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                <span className={`flex-shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full text-[10px] font-bold ${isSelected ? "bg-[#E5A93D] text-black" : "bg-[#E5A93D]/15 text-[#E5A93D] border border-[#E5A93D]/30"}`}>
                                    {i + 1}
                                </span>
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-zinc-950">
                                        {format(new Date(s.date + "T00:00:00"), "EEEE d MMMM yyyy", { locale: nl })}
                                    </div>
                                    <div className="text-xs text-zinc-500">{s.time}</div>
                                </div>
                            </div>
                            {isSelected ? (
                                <span className="text-[10px] uppercase tracking-wider font-bold text-[#E5A93D] flex items-center gap-1 flex-shrink-0">
                                    <Check className="h-3 w-3" /> Gekozen
                                </span>
                            ) : (
                                <span className="text-[11px] text-zinc-500 flex-shrink-0">Klik om in te plannen →</span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    ) : null;

    const statusEditor = (
        <div className="bg-[#FFFFFF] border border-[#E5A93D]/30 rounded-md p-6" data-testid="status-editor">
            <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="h-4 w-4 text-[#E5A93D]" />
                <h3 className="font-display font-bold text-lg tracking-tight">Status beheren</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Nieuwe status</Label>
                    <Select value={newStatus} onValueChange={setNewStatus}>
                        <SelectTrigger className="h-11 bg-[#F4F4F5] border-zinc-200 rounded-sm" data-testid="status-select">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#FFFFFF] border-zinc-200">
                            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                                <SelectItem key={k} value={k}>{v.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Geplande datum</Label>
                    <Popover>
                        <PopoverTrigger asChild>
                            <button className="w-full h-11 px-3 flex items-center gap-2 bg-[#F4F4F5] border border-zinc-200 rounded-sm text-left text-sm" data-testid="sched-date-trigger">
                                <Calendar className="h-4 w-4 text-zinc-500" />
                                <span className={schedDate ? "text-zinc-950" : "text-zinc-500"}>
                                    {schedDate ? format(schedDate, "PPP", { locale: nl }) : "Kies datum"}
                                </span>
                            </button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 bg-[#FFFFFF] border-zinc-200">
                            <Cal mode="single" selected={schedDate} onSelect={setSchedDate} locale={nl} initialFocus />
                        </PopoverContent>
                    </Popover>
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Gepland uur</Label>
                    <TimePicker
                        value={schedTime}
                        onChange={setSchedTime}
                        data-testid="sched-time-trigger"
                    />
                </div>
                <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Notitie voor makelaar</Label>
                    <Textarea rows={3} value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]" data-testid="admin-notes" />
                </div>
                {req.requires_mm_approval && req.mm_approved !== true && (
                    <div className="md:col-span-2" data-testid="mm-override-block">
                        <label className="flex items-start gap-3 p-3 bg-amber-500/5 border border-amber-500/30 rounded-sm cursor-pointer hover:bg-amber-500/10 transition">
                            <input
                                type="checkbox"
                                checked={overrideMm}
                                onChange={(e) => setOverrideMm(e.target.checked)}
                                className="mt-0.5 h-4 w-4 accent-[#E5A93D] flex-shrink-0"
                                data-testid="mm-override-checkbox"
                            />
                            <div className="text-sm">
                                <div className="text-amber-700 font-semibold">MM-goedkeuring overslaan</div>
                                <div className="text-xs text-zinc-500 mt-0.5">Deze aanvraag vereist normaal goedkeuring van de marketing manager. Vink aan om het project toch te plannen / in productie te zetten zonder MM-akkoord. Dit wordt geregistreerd in het logboek.</div>
                            </div>
                        </label>
                    </div>
                )}
                <Button onClick={saveStatus} disabled={savingStatus} className="md:col-span-2 w-full bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98]" data-testid="save-status-btn">
                    {savingStatus ? "Opslaan..." : "Status opslaan"}
                </Button>
            </div>
        </div>
    );

    const productsBlock = (
        <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
                <h2 className="font-display font-bold text-xl tracking-tight">Geboekte formules</h2>
                <button
                    onClick={() => setItemsEditOpen(true)}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-[#E5A93D]/40 text-[#E5A93D] hover:bg-[#E5A93D]/10 rounded-sm transition active:scale-95"
                    data-testid="edit-items-btn"
                >
                    <Plus className="h-3.5 w-3.5" /> Voeg formule / optie toe
                </button>
            </div>
            <div className="space-y-3">
                {req.items.map((it, idx) => {
                    const p = products.find((x) => x.id === it.product_id);
                    if (!p) return null;
                    const photoCount = req.photo_urls?.length || 0;
                    let basePrice = p.price;
                    if (p.photo_tiers) {
                        const tier = p.photo_tiers.find((t) => photoCount >= t.min_photos && photoCount <= t.max_photos);
                        if (tier) basePrice = tier.price;
                    }
                    const selectedOptions = (it.option_ids || []).map((oid) => p.options?.find((o) => o.id === oid)).filter(Boolean);
                    return (
                        <React.Fragment key={idx}>
                            <div className="flex items-start justify-between py-3 border-b border-zinc-100">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{p.name}</div>
                                    <div className="font-medium text-zinc-950">{p.tagline}</div>
                                </div>
                                <div className="text-[#E5A93D] font-bold">{pricesHidden ? "€ ••••" : `€${basePrice.toFixed(2)}`}</div>
                            </div>
                            {selectedOptions.map((o) => (
                                <div key={o.id} className="flex items-start justify-between py-2 pl-6 border-b border-zinc-100">
                                    <div className="text-sm text-zinc-600">+ {o.label}</div>
                                    <div className="text-[#E5A93D] font-medium">{pricesHidden ? "€ ••••" : `€${Number(o.price).toFixed(2)}`}</div>
                                </div>
                            ))}
                        </React.Fragment>
                    );
                })}
                {(req.custom_items || []).map((ci, idx) => (
                    <div key={ci.id || idx} className="flex items-start justify-between py-3 border-b border-zinc-100" data-testid={`custom-booked-item-${idx}`}>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Extra</div>
                            <div className="font-medium text-zinc-950">{ci.label}</div>
                        </div>
                        <div className="text-[#E5A93D] font-bold">{pricesHidden ? "€ ••••" : `€${Number(ci.price).toFixed(2)}`}</div>
                    </div>
                ))}
                {(req.travel_cost > 0 || req.travel_distance_km > 0) && (
                    <div className="flex items-start justify-between py-3 border-b border-zinc-100">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Verplaatsing</div>
                            <div className="font-medium text-zinc-950">{req.travel_distance_km} km</div>
                        </div>
                        <div className="text-[#E5A93D] font-bold">{req.travel_cost > 0 ? (pricesHidden ? "€ ••••" : `€${req.travel_cost.toFixed(2)}`) : "Inbegrepen"}</div>
                    </div>
                )}
                <div className="flex items-start justify-between py-3 pt-4">
                    <div className="font-display font-bold text-lg">Totaal</div>
                    <div className="text-[#E5A93D] font-display font-black text-2xl">{pricesHidden ? "€ ••••" : `€${req.total_price}`}</div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 font-body pb-20" data-testid="admin-project-detail">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-10">
                <div className="flex items-center justify-between mb-6">
                    <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950" data-testid="admin-back">
                        <ArrowLeft className="h-4 w-4" /> Terug naar overzicht
                    </Link>
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <button
                                className="inline-flex items-center justify-center h-9 w-9 rounded-sm border border-zinc-200 text-zinc-600 hover:border-[#E5A93D]/40 hover:text-zinc-950 transition active:scale-95"
                                aria-label="Meer acties"
                                data-testid="project-actions-trigger"
                            >
                                <MoreVertical className="h-4 w-4" />
                            </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56 bg-[#FFFFFF] border-zinc-200 text-zinc-950">
                            <DropdownMenuItem
                                onClick={() => {
                                    const safe = (req.address_street || req.id).replace(/[^\w-]/g, "_");
                                    downloadAuthed(`/admin/requests/${req.id}/invoice`, `Factuur_${safe}.pdf`);
                                }}
                                className="cursor-pointer focus:bg-[#E5A93D]/10 focus:text-[#E5A93D] gap-2"
                                data-testid="download-invoice-btn"
                            >
                                <FileText className="h-4 w-4 text-[#E5A93D]" />
                                <span>Factuur (PDF)</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => {
                                    const safe = (req.address_street || req.id).replace(/[^\w-]/g, "_");
                                    zipDl.start(`/requests/${req.id}/project-zip`, `${safe}.zip`);
                                }}
                                className="cursor-pointer focus:bg-emerald-500/10 focus:text-emerald-600 gap-2"
                                data-testid="download-project-zip-btn"
                            >
                                <Download className="h-4 w-4 text-emerald-600" />
                                <span>Alles downloaden</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-zinc-100" />
                            <DropdownMenuItem
                                onClick={() => { setDeleteInput(""); setDeleteOpen(true); }}
                                className="cursor-pointer text-red-600 focus:bg-red-500/10 focus:text-red-700 gap-2"
                                data-testid="delete-project-btn"
                            >
                                <Trash2 className="h-4 w-4" />
                                <span>Verwijder project</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
                    <div>
                        <div className="flex items-center gap-3 mb-4 flex-wrap">
                            <StatusBadge status={req.status} />
                            {req.preview_video_url && (
                                req.preview_approved ? (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wider border border-emerald-500/30 bg-emerald-500/10 text-emerald-600" data-testid="admin-preview-approved">
                                        <CheckCircle2 className="h-3 w-3" /> Preview goedgekeurd
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wider border border-[#E5A93D]/30 bg-[#E5A93D]/10 text-[#E5A93D]">
                                        Preview in review
                                    </span>
                                )
                            )}
                            {req.reel_preview_url && (
                                req.reel_preview_approved ? (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wider border border-emerald-500/30 bg-emerald-500/10 text-emerald-600" data-testid="admin-reel-preview-approved">
                                        <CheckCircle2 className="h-3 w-3" /> Reel preview goedgekeurd
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[10px] font-bold uppercase tracking-wider border border-[#E5A93D]/30 bg-[#E5A93D]/10 text-[#E5A93D]">
                                        Reel preview in review
                                    </span>
                                )
                            )}
                            <span className="text-xs text-zinc-500">Makelaar: <span className="text-[#E5A93D]">{req.user_name}</span></span>
                        </div>
                        <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-none">
                            {req.address_street}
                        </h1>
                        <p className="text-zinc-500 mt-2">{req.address_zip} {req.address_city}</p>
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                            <ProjectShareButton req={req} onUpdated={(updated) => setReq(updated)} />
                            <LocationWeatherButton address={`${req.address_street}, ${req.address_zip || ""} ${req.address_city}`.trim()} />
                            <button
                                type="button"
                                onClick={() => setEditDetailsOpen(true)}
                                className="inline-flex items-center gap-2 h-8 px-3 rounded-sm text-xs font-medium border border-zinc-200 bg-[#FFFFFF] text-zinc-600 hover:text-zinc-950 hover:border-[#E5A93D]/40 hover:bg-[#E5A93D]/5 transition active:scale-[0.98]"
                                data-testid="edit-details-btn"
                            >
                                <Pencil className="h-3.5 w-3.5 text-[#E5A93D]" />
                                Aanvraag bewerken
                            </button>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Totaal</div>
                        <div className="font-display font-black text-4xl text-[#E5A93D]" data-testid="project-total-price">{pricesHidden ? "€ ••••" : `€${req.total_price}`}</div>
                        {(req.ai_edits_count || 0) > 0 && (
                            <div
                                className="mt-3 inline-flex items-stretch border border-[#7C3AED]/40 bg-[#7C3AED]/10 rounded-sm overflow-hidden text-left"
                                data-testid="ai-cost-summary"
                                title="Totaal aan AI-bewerkingen voor dit project — gebruik voor facturatie aan de makelaar."
                            >
                                <div className="px-3 py-1.5 border-r border-[#7C3AED]/30">
                                    <div className="text-[9px] uppercase tracking-wider text-[#7C3AED] flex items-center gap-1"><Sparkles className="h-2.5 w-2.5" /> AI-edits</div>
                                    <div className="font-display font-black text-lg text-zinc-950 leading-tight">{req.ai_edits_count}</div>
                                </div>
                                <div className="px-3 py-1.5 border-r border-[#7C3AED]/30">
                                    <div className="text-[9px] uppercase tracking-wider text-zinc-500">Vervangen</div>
                                    <div className="font-display font-black text-lg text-emerald-600 leading-tight">{req.ai_approved_count || 0}</div>
                                </div>
                                <div className="px-3 py-1.5">
                                    <div className="text-[9px] uppercase tracking-wider text-zinc-500">AI-kost</div>
                                    <div className="font-display font-black text-lg text-[#7C3AED] leading-tight">{pricesHidden ? "€ ••••" : `€${Number(req.ai_total_cost_eur || 0).toFixed(2)}`}</div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Left — info */}
                    <div className="lg:col-span-2 space-y-6">
                        <MMApprovalPanel req={req} currentUser={user} onUpdated={(updated) => setReq(updated)} />
                        {/* Agent logo */}
                        {req.user_logo_url && (
                            <div className="bg-[#FFFFFF] border border-[#E5A93D]/20 rounded-md p-6 flex items-center gap-6" data-testid="agent-logo-section">
                                <div className="h-24 w-24 bg-zinc-100 border border-zinc-200 rounded-md flex items-center justify-center overflow-hidden flex-shrink-0">
                                    {req.user_logo_url.toLowerCase().endsWith(".pdf") ? (
                                        <a href={req.user_logo_url.startsWith("http") ? req.user_logo_url : `${BACKEND}${req.user_logo_url}`} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 text-[#E5A93D]">
                                            <FileText className="h-8 w-8" />
                                            <span className="text-[10px] uppercase tracking-wider">Open PDF</span>
                                        </a>
                                    ) : (
                                        <img src={req.user_logo_url.startsWith("http") ? req.user_logo_url : `${BACKEND}${req.user_logo_url}`} alt="logo" className="max-h-full max-w-full object-contain p-2" />
                                    )}
                                </div>
                                <div className="flex-1">
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Logo makelaar — te gebruiken in video</div>
                                    <div className="font-display font-bold text-lg text-zinc-950">{req.user_company || req.user_name}</div>
                                    <button onClick={() => downloadStatic(req.user_logo_url, req.user_logo_url.split("/").pop())} className="text-xs text-[#E5A93D] hover:underline" data-testid="download-logo">
                                        Logo downloaden
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Co-agents (multi-makelaar shoots) */}
                        <CoAgentsManager req={req} onSaved={(updated) => setReq(updated)} />

                        {preferredSlotsBlock}

                        {statusEditor}

                        {/* Beschrijving */}
                        <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8">
                            <div className="flex items-center justify-between mb-4 gap-3">
                                <div className="flex items-center gap-2">
                                    <FileText className="h-4 w-4 text-[#E5A93D]" />
                                    <h2 className="font-display font-bold text-xl tracking-tight">Beschrijving</h2>
                                </div>
                                {!editingDescription && (
                                    <button
                                        onClick={startEditDescription}
                                        className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-950 border border-zinc-200 rounded-sm h-8 px-3 hover:bg-zinc-100 transition"
                                        data-testid="edit-description-btn"
                                    >
                                        <Pencil className="h-3 w-3" /> Bewerken
                                    </button>
                                )}
                            </div>
                            {editingDescription ? (
                                <div className="space-y-3" data-testid="edit-description-block">
                                    <textarea
                                        value={descriptionDraft}
                                        onChange={(e) => setDescriptionDraft(e.target.value)}
                                        rows={6}
                                        className="w-full bg-[#F4F4F5] border border-zinc-200 rounded-sm text-sm text-zinc-950 p-3 focus:outline-none focus:border-[#E5A93D]/40"
                                        placeholder="Vertel iets over het pand: troeven, sfeer, ligging…"
                                        data-testid="description-textarea"
                                    />
                                    <div className="flex items-center gap-2">
                                        <Button
                                            onClick={saveDescription}
                                            disabled={savingDescription}
                                            className="bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm h-9 px-4"
                                            data-testid="description-save"
                                        >
                                            {savingDescription ? "Opslaan…" : "Opslaan"}
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            onClick={() => setEditingDescription(false)}
                                            disabled={savingDescription}
                                            className="text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100 rounded-sm h-9 px-4"
                                            data-testid="description-cancel"
                                        >
                                            Annuleren
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <p className="text-sm text-zinc-600 whitespace-pre-wrap leading-relaxed">{req.description || <span className="text-zinc-500">Geen beschrijving</span>}</p>
                            )}
                            {req.property_link && (
                                <a href={req.property_link} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-sm text-[#E5A93D] hover:underline">
                                    <LinkIcon className="h-3.5 w-3.5" /> {req.property_link}
                                </a>
                            )}
                            {req.photo_urls?.length > 0 && (
                                <div className="mt-6">
                                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                                        <div className="text-xs uppercase tracking-wider text-zinc-500">Foto's door makelaar ({req.photo_urls.length})</div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <button
                                                onClick={async () => {
                                                    const n = req.photo_urls.length;
                                                    if (!window.confirm(`${n} foto${n === 1 ? "" : "'s"} verplaatsen naar Fotoreeks?\n\nDe foto's verschijnen dan in Fotoreeks waar je ze met AI kunt bewerken.\n"Foto's door makelaar" wordt leeggemaakt.`)) return;
                                                    try {
                                                        const { data } = await api.post(`/requests/${req.id}/photos/move-to-fotoreeks`);
                                                        toast.success(`${data.moved} foto's verplaatst naar Fotoreeks`);
                                                        load();
                                                    } catch (e) {
                                                        toast.error(e.response?.data?.detail || "Verplaatsen mislukt");
                                                    }
                                                }}
                                                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-[#7C3AED]/40 text-[#7C3AED] hover:bg-[#7C3AED]/10 rounded-sm active:scale-[0.98]"
                                                data-testid="move-photos-to-fotoreeks-btn"
                                                title="Verplaats alle makelaar-foto's naar Fotoreeks zodat je ze met AI kunt bewerken"
                                            >
                                                <Sparkles className="h-3.5 w-3.5" /> Verplaats naar Fotoreeks
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const safe = (req.address_street || req.id).replace(/[^a-z0-9]/gi, "_");
                                                    folderDl.start({
                                                        folderName: `${safe}_fotos`,
                                                        files: buildAgentPhotoList(req, safe),
                                                    });
                                                }}
                                                disabled={folderDl.state.active}
                                                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none"
                                                data-testid="download-all-photos-btn"
                                            >
                                                <Download className="h-3.5 w-3.5" /> Alle foto's downloaden
                                            </button>
                                        </div>
                                    </div>
                                    <PhotoGallery
                                        requestId={req.id}
                                        photos={req.photo_urls}
                                        field="photo_urls"
                                        canDelete={true}
                                        onDeleted={load}
                                        testidPrefix="agent-photo"
                                    />
                                </div>
                            )}
                            {req.document_urls?.length > 0 && (
                                <div className="mt-6">
                                    <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Documenten ({req.document_urls.length})</div>
                                    <div className="space-y-2">
                                        {req.document_urls.map((u) => {
                                            const url = typeof u === "string" ? u : u.url;
                                            const name = typeof u === "string" ? url.split("/").pop() : u.name;
                                            return (
                                                <button
                                                    type="button"
                                                    key={url}
                                                    onClick={() => downloadStatic(url, name)}
                                                    className="w-full text-left flex items-center gap-3 p-3 bg-[#F4F4F5] border border-zinc-200 hover:border-[#E5A93D]/50 rounded-sm transition-colors"
                                                    data-testid={`admin-doc-${url}`}
                                                >
                                                    <FileText className="h-4 w-4 text-[#E5A93D]" />
                                                    <span className="text-sm text-zinc-950 flex-1 truncate">{name}</span>
                                                    <span className="inline-flex items-center gap-1 text-xs text-[#E5A93D]"><Download className="h-3 w-3" /> Download</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>

                        <AdditionalLinksPanel
                            requestId={req.id}
                            links={req.additional_links || []}
                            onChange={load}
                            currentUser={user}
                        />

                        <ScriptEditor
                            requestId={req.id}
                            initialScript={req.script}
                            initialBlocks={req.script_blocks}
                            updatedAt={req.script_updated_at}
                            updatedBy={req.script_updated_by}
                            items={req.items}
                            products={products}
                            onChange={(updated) => setReq(updated)}
                        />

                        {/* Deliverables editor */}
                        {/* Live preview + comments (visible to admin too, can post + reply) */}
                        {req.preview_video_url && (
                            <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8" data-testid="admin-preview-section">
                                <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                                    <div className="flex items-center gap-2">
                                        <FilmIcon className="h-4 w-4 text-[#E5A93D]" />
                                        <h2 className="font-display font-bold text-xl tracking-tight">Preview video</h2>
                                    </div>
                                    <PreviewShareButton req={req} onUpdated={(updated) => setReq(updated)} />
                                </div>
                                <CommentablePreview videoUrl={req.preview_video_url} requestId={req.id} currentUser={user} />
                            </div>
                        )}

                        {req.reel_preview_url && (
                            <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8" data-testid="admin-reel-preview-section">
                                <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                                    <div className="flex items-center gap-2">
                                        <FilmIcon className="h-4 w-4 text-[#E5A93D]" />
                                        <h2 className="font-display font-bold text-xl tracking-tight">Reel preview — opmerkingen</h2>
                                    </div>
                                    <PreviewShareButton req={req} asset="reel_preview" onUpdated={(updated) => setReq(updated)} />
                                </div>
                                <CommentablePreview videoUrl={req.reel_preview_url} requestId={req.id} currentUser={user} assetType="reel_preview" />
                            </div>
                        )}

                        {/* Final + reel commentable for admin too */}
                        {req.final_video_url && (
                            <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8" data-testid="admin-final-section">
                                <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                                    <div className="flex items-center gap-2">
                                        <FilmIcon className="h-4 w-4 text-[#E5A93D]" />
                                        <h2 className="font-display font-bold text-xl tracking-tight">Finale video — opmerkingen</h2>
                                    </div>
                                    <PreviewShareButton req={req} asset="final_video" onUpdated={(updated) => setReq(updated)} />
                                </div>
                                <CommentablePreview videoUrl={req.final_video_url} requestId={req.id} currentUser={user} assetType="final" />
                            </div>
                        )}
                        {req.reel_url && (
                            <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8" data-testid="admin-reel-section">
                                <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                                    <div className="flex items-center gap-2">
                                        <FilmIcon className="h-4 w-4 text-[#E5A93D]" />
                                        <h2 className="font-display font-bold text-xl tracking-tight">Reel — opmerkingen</h2>
                                    </div>
                                    <PreviewShareButton req={req} asset="reel" onUpdated={(updated) => setReq(updated)} />
                                </div>
                                <CommentablePreview videoUrl={req.reel_url} requestId={req.id} currentUser={user} assetType="reel" />
                            </div>
                        )}

                        {req.ai_photo_urls && req.ai_photo_urls.length > 0 && (() => {
                            // Group annotation feedback comments by photo_index for badges + lightbox
                            const aiPhotoComments = (comments || []).filter(
                                (c) => (c.asset_type === "ai_photos") && (c.photo_index !== null && c.photo_index !== undefined),
                            );
                            const commentsByPhoto = aiPhotoComments.reduce((map, c) => {
                                (map[c.photo_index] = map[c.photo_index] || []).push(c);
                                return map;
                            }, {});
                            const lifetimeTotal = req.ai_photos_feedback_total || 0;
                            const openTotal = aiPhotoComments.length;
                            return (
                            <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8" data-testid="admin-ai-photos-section">
                                <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <FilmIcon className="h-4 w-4 text-[#E5A93D]" />
                                        <h2 className="font-display font-bold text-xl tracking-tight">Fotoreeks — opmerkingen</h2>
                                        {lifetimeTotal > 0 && (
                                            <span
                                                className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider bg-[#E5A93D]/15 border border-[#E5A93D]/40 text-[#E5A93D] rounded-sm px-2 h-6"
                                                data-testid="ai-photos-feedback-total"
                                                title="Totaal aantal feedback-opmerkingen ontvangen voor deze fotoreeks (voor facturatie). Wordt nooit verlaagd, zelfs niet na vervangen."
                                            >
                                                {lifetimeTotal} totaal ontvangen
                                                {openTotal > 0 && openTotal !== lifetimeTotal && (
                                                    <span className="text-zinc-500 normal-case tracking-normal">· {openTotal} open</span>
                                                )}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={async () => {
                                                if (!window.confirm("Badges herstellen?\n\nDit herrekent welke AI-edits, reels en video's bij welke foto horen door URLs te matchen. JE WERK BLIJFT BEHOUDEN — alleen de paringen worden hersteld.\n\nDoorgaan?")) return;
                                                try {
                                                    const { data } = await api.post(`/admin/requests/${req.id}/repair-photo-indices`);
                                                    toast.success(`Hersteld · ${data.rebuilt_previous_urls} AI-paren · ${data.rebuilt_reels} reels · ${data.rebuilt_reels_original} orig-reels`);
                                                    window.location.reload();
                                                } catch (e) {
                                                    toast.error(e.response?.data?.detail || "Repair mislukt");
                                                }
                                            }}
                                            className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-red-500/15 hover:bg-red-500/30 border border-red-500/40 text-red-700 rounded-sm transition active:scale-95"
                                            data-testid="repair-photo-indices-btn"
                                            title="Voer dit uit als badges (AI/video/reel) op de verkeerde foto's verschijnen — gebeurt na het verwijderen/herorderen van foto's in een ouder project. NIEUWE deletes hebben dit niet meer nodig."
                                        >
                                            <Wrench className="h-3.5 w-3.5" /> Repair badges
                                        </button>
                                        {(() => {
                                            const allVideos = Object.values(videoShotsByPhoto).flat().filter((j) => j.status === "completed" && j.result_url);
                                            if (allVideos.length === 0) return null;
                                            const slug = (s) => (s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").replace(/[^\w\s.-]/g, "").replace(/\s+/g, "_").replace(/_+/g, "_").replace(/^_+|_+$/g, "").slice(0, 60) || "project";
                                            const addr = slug([req.address_street, req.address_city].filter(Boolean).join(" "));
                                            const downloadAll = async () => {
                                                // Sort by photo_index then by created_at so videos come out in a
                                                // predictable Final Cut Pro timeline order.
                                                const sorted = [...allVideos].sort((a, b) => (
                                                    (a.photo_index ?? 0) - (b.photo_index ?? 0)
                                                    || String(a.created_at || "").localeCompare(String(b.created_at || ""))
                                                ));
                                                toast.info(`Start download van ${sorted.length} videoshot${sorted.length === 1 ? "" : "s"} — duurt enkele seconden`);
                                                for (let i = 0; i < sorted.length; i += 1) {
                                                    const j = sorted[i];
                                                    const n = (j.photo_index ?? 0) >= 0 ? String((j.photo_index ?? 0) + 1).padStart(2, "0") : "XX";
                                                    const order = String(i + 1).padStart(2, "0");
                                                    const preset = (j.preset_key || "shot").replace(/_/g, "-");
                                                    const name = `${order} - ${addr ? addr + " - " : ""}foto ${n} - ${preset} - SocialFactoryAI.mp4`;
                                                    downloadStatic(j.result_url, name);
                                                    // Small delay between downloads so the browser actually fires
                                                    // each save dialog (and to play nice with R2 redirect rate).
                                                    // eslint-disable-next-line no-await-in-loop
                                                    await new Promise((r) => setTimeout(r, 700));
                                                }
                                                toast.success("Alle videoshots gedownload");
                                            };
                                            return (
                                                <button
                                                    type="button"
                                                    onClick={downloadAll}
                                                    className="inline-flex items-center gap-1.5 text-xs px-3 h-9 bg-sky-500 hover:bg-sky-400 text-black font-bold rounded-sm transition active:scale-95"
                                                    data-testid="download-all-videoshots-btn"
                                                    title={`Download alle ${allVideos.length} videoshots voor Final Cut Pro`}
                                                >
                                                    <FilmIcon className="h-3.5 w-3.5" /> Download alle video's ({allVideos.length})
                                                </button>
                                            );
                                        })()}
                                        <PreviewShareButton req={req} asset="photos" onUpdated={(updated) => setReq(updated)} />
                                    </div>
                                </div>
                                {(videoShotsByPhoto[-1] || []).length > 0 && (
                                    <div className="mb-4 flex items-center justify-between gap-3 px-4 py-3 bg-amber-500/10 border border-amber-500/40 rounded-sm" data-testid="orphan-videoshots-banner">
                                        <div className="text-sm text-amber-800">
                                            <span className="font-bold">{videoShotsByPhoto[-1].length} videoshot{videoShotsByPhoto[-1].length === 1 ? "" : "s"}</span> niet meer gekoppeld aan een foto (bronfoto vervangen of verwijderd) — nog steeds beschikbaar om te bekijken en downloaden.
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => setVideoGalleryIdx(-1)}
                                            className="shrink-0 inline-flex items-center gap-1.5 text-xs px-3 h-8 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-sm transition active:scale-95"
                                            data-testid="orphan-videoshots-open-btn"
                                        >
                                            <FilmIcon className="h-3.5 w-3.5" /> Bekijken
                                        </button>
                                    </div>
                                )}
                                {(() => {
                                    const webSel = (req.website_selection || []).filter((u) => (req.ai_photo_urls || []).includes(u));
                                    const toggleWeb = async (url) => {
                                        const next = webSel.includes(url) ? webSel.filter((x) => x !== url) : [...webSel, url];
                                        try {
                                            await api.post(`/requests/${req.id}/website-selection`, { urls: next });
                                            toast.success(webSel.includes(url) ? "Uit Selectie website gehaald" : "Toegevoegd aan Selectie website");
                                            load();
                                        } catch (e) {
                                            toast.error(e.response?.data?.detail || "Opslaan mislukt");
                                        }
                                    };
                                    return (
                                        <>
                                            <div className="flex items-center gap-2 mb-3 mt-1" data-testid="photo-tabs">
                                                <button
                                                    type="button"
                                                    onClick={() => setPhotoTab("all")}
                                                    className={`h-8 px-3 rounded-sm text-[11px] font-bold uppercase tracking-wider transition ${photoTab === "all" ? "bg-[#E5A93D] text-black" : "bg-white border border-zinc-200 text-zinc-500 hover:text-zinc-950"}`}
                                                    data-testid="photo-tab-all"
                                                >
                                                    Alle foto's ({(req.ai_photo_urls || []).length})
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setPhotoTab("web")}
                                                    className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-sm text-[11px] font-bold uppercase tracking-wider transition ${photoTab === "web" ? "bg-[#E5A93D] text-black" : "bg-white border border-zinc-200 text-zinc-500 hover:text-zinc-950"}`}
                                                    data-testid="photo-tab-web"
                                                >
                                                    <Globe className="h-3 w-3" /> Selectie website ({webSel.length})
                                                </button>
                                            </div>
                                            {photoTab === "web" ? (
                                                <WebsiteSelectionGrid requestId={req.id} selection={webSel} onChanged={load} />
                                            ) : (
                                <PhotoGallery
                                    requestId={req.id}
                                    photos={req.ai_photo_urls}
                                    field="ai_photo_urls"
                                    canDelete={true}
                                    onDeleted={load}
                                    canReorder={true}
                                    onReordered={load}
                                    webSelection={webSel}
                                    onToggleWeb={toggleWeb}
                                    testidPrefix="ai-photo"
                                    onPhotoClick={(i) => setAiLightboxIdx(i)}
                                    badgeByIndex={(i) => {
                                        const n = (commentsByPhoto[i] || []).length;
                                        const vc = (videoShotsByPhoto[i] || []).length;
                                        const ac = activeVideoShotsByPhoto[i] || 0;
                                        const aic = activeAiEditsByPhoto[i] || 0;
                                        const isAiEdited = !!(req?.ai_photo_previous_urls && req.ai_photo_previous_urls[String(i)]);
                                        const hasReel = !!(req?.reel_photo_urls && req.reel_photo_urls[String(i)]);
                                        if (!n && !vc && !ac && !aic && !isAiEdited && !hasReel) return null;
                                        return (
                                            <div key={`badges-${i}`} className="absolute top-1.5 right-1.5 flex flex-col items-end gap-1">
                                                {n > 0 && (
                                                    <span
                                                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#E5A93D] text-black rounded-sm px-1.5 h-5"
                                                        data-testid={`ai-photo-badge-${i}`}
                                                        title={`${n} opmerking${n === 1 ? "" : "en"} op deze foto`}
                                                    >
                                                        <MessageSquare className="h-3 w-3" />
                                                        {n}
                                                    </span>
                                                )}
                                                {isAiEdited && (
                                                    <span
                                                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-[#E5A93D] to-[#F4C842] text-black rounded-sm px-1.5 h-5 shadow-[0_0_8px_rgba(229,169,61,0.5)]"
                                                        data-testid={`ai-photo-ai-badge-${i}`}
                                                        title="Deze foto is AI-bewerkt"
                                                    >
                                                        <Sparkles className="h-3 w-3" />
                                                        AI
                                                    </span>
                                                )}
                                                {hasReel && (
                                                    <span
                                                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-fuchsia-500 to-pink-500 text-zinc-950 rounded-sm px-1.5 h-5 shadow-[0_0_8px_rgba(232,121,249,0.5)]"
                                                        data-testid={`ai-photo-reel-badge-${i}`}
                                                        title="Deze foto heeft een 9:16 reel-versie"
                                                    >
                                                        <FilmIcon className="h-3 w-3" />
                                                        REEL
                                                    </span>
                                                )}
                                                {aic > 0 && (
                                                    <span
                                                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#E5A93D] text-black rounded-sm px-1.5 h-5 animate-pulse shadow-[0_0_10px_rgba(229,169,61,0.6)] ring-1 ring-[#E5A93D]/60"
                                                        data-testid={`ai-photo-edit-pulse-badge-${i}`}
                                                        title={`AI retouche${aic === 1 ? "" : "s"} aan't renderen`}
                                                    >
                                                        <Sparkles className="h-3 w-3 animate-spin" style={{ animationDuration: "2s" }} />
                                                        {aic}
                                                    </span>
                                                )}
                                                {ac > 0 && (
                                                    <span
                                                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-sky-500 text-black rounded-sm px-1.5 h-5 animate-pulse shadow-[0_0_10px_rgba(56,189,248,0.6)] ring-1 ring-sky-300/60"
                                                        data-testid={`ai-photo-video-pulse-badge-${i}`}
                                                        title={`${ac} videoshot${ac === 1 ? "" : "s"} aan't genereren`}
                                                    >
                                                        <Loader2 className="h-3 w-3 animate-spin" />
                                                        {ac}
                                                    </span>
                                                )}
                                                {vc > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setVideoGalleryIdx(i); }}
                                                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-sky-500 hover:bg-sky-400 text-black rounded-sm px-1.5 h-5 transition active:scale-95"
                                                        data-testid={`ai-photo-video-badge-${i}`}
                                                        title={`Open ${vc} videoshot${vc === 1 ? "" : "s"} voor deze foto`}
                                                    >
                                                        <FilmIcon className="h-3 w-3" />
                                                        {vc}
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    }}
                                />
                                            )}
                                        </>
                                    );
                                })()}
                                <div className="mt-4">
                                    <AssetComments requestId={req.id} currentUser={user} assetType="ai_photos" title="Opmerkingen op fotoreeks" />
                                </div>
                                <AdminAiPhotoLightbox
                                    requestId={req.id}
                                    photos={req.ai_photo_urls}
                                    openIndex={aiLightboxIdx}
                                    onClose={() => setAiLightboxIdx(null)}
                                    onReplaced={() => { load(); }}
                                    commentsByPhoto={commentsByPhoto}
                                    previousUrls={req.ai_photo_previous_urls || {}}
                                    reelUrls={req.reel_photo_urls || {}}
                                    reelOriginalUrls={req.reel_original_photo_urls || {}}
                                    addressLabel={[req.address_street, req.address_city].filter(Boolean).join(" ")}
                                    onOpenVideoGallery={(i) => setVideoGalleryIdx(i)}
                                    onOpenAiHistory={(i, jobs) => setAiHistoryState({ open: true, photoIdx: i, jobs })}
                                />
                                <VideoShotGalleryModal
                                    open={videoGalleryIdx !== null}
                                    onClose={() => setVideoGalleryIdx(null)}
                                    photoIndex={videoGalleryIdx ?? 0}
                                    jobs={videoShotsByPhoto[videoGalleryIdx] || []}
                                    addressLabel={[req.address_street, req.address_city].filter(Boolean).join(" ")}
                                    onDelete={async (jobId) => {
                                        // Real API call — let errors propagate so the modal can toast them
                                        await api.delete(`/video-shots/${jobId}`);
                                        // Optimistically remove from local state
                                        setVideoShotsByPhoto((prev) => {
                                            const next = { ...prev };
                                            if (videoGalleryIdx != null && next[videoGalleryIdx]) {
                                                next[videoGalleryIdx] = next[videoGalleryIdx].filter((x) => x.id !== jobId);
                                                if (next[videoGalleryIdx].length === 0) {
                                                    delete next[videoGalleryIdx];
                                                }
                                            }
                                            return next;
                                        });
                                    }}
                                />
                                <AiEditHistoryModal
                                    open={aiHistoryState.open}
                                    onClose={() => setAiHistoryState({ open: false, photoIdx: null, jobs: [] })}
                                    requestId={req.id}
                                    photoIndex={aiHistoryState.photoIdx ?? 0}
                                    originalUrl={(() => {
                                        if (aiHistoryState.photoIdx == null) return null;
                                        const idx = aiHistoryState.photoIdx;
                                        const direct = (req.ai_photo_previous_urls || {})[String(idx)];
                                        if (direct) return direct;
                                        // Fallback: derive the genuine original from the FIRST
                                        // completed job in this photo's history (its source_url
                                        // is what was active before any AI edit). Covers legacy
                                        // projects where the previous_urls mapping was wiped
                                        // by an older revert/set-active code path.
                                        const photoJobs = (aiHistoryState.jobs || [])
                                            .filter((j) => j.photo_index === idx && j.status === "completed")
                                            .sort((a, b) => String(a.created_at || "").localeCompare(String(b.created_at || "")));
                                        return photoJobs[0]?.source_url || null;
                                    })()}
                                    currentUrl={
                                        aiHistoryState.photoIdx != null
                                            ? (req.ai_photo_urls || [])[aiHistoryState.photoIdx]
                                            : null
                                    }
                                    jobs={aiHistoryState.jobs}
                                    addressLabel={[req.address_street, req.address_city].filter(Boolean).join(" ")}
                                    onActivated={() => {
                                        // Reload project to reflect the new active AI photo URL
                                        load();
                                        setAiHistoryState({ open: false, photoIdx: null, jobs: [] });
                                    }}
                                    onReplaced={() => {
                                        // Reload project but keep the modal open so the user sees the fresh state
                                        load();
                                    }}
                                />
                            </div>
                            );
                        })()}

                        <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8" data-testid="deliverables-editor">
                            <div className="flex items-center gap-2 mb-6">
                                <FilmIcon className="h-4 w-4 text-[#E5A93D]" />
                                <h2 className="font-display font-bold text-xl tracking-tight">Media uploaden</h2>
                            </div>
                            <div className="space-y-4">
                                <VideoField
                                    label="Preview video (ter goedkeuring door makelaar)"
                                    testid="preview-video"
                                    url={previewVideoUrl}
                                    setUrl={setPreviewVideoUrl}
                                    progress={uploadingPreview}
                                    onFile={(f) => uploadVideoFile(f, setUploadingPreview, setPreviewVideoUrl, "preview")}
                                    backend={BACKEND}
                                />
                                <VideoField
                                    label="Finale video (4K)"
                                    testid="final-video"
                                    url={finalVideoUrl}
                                    setUrl={setFinalVideoUrl}
                                    progress={uploadingFinal}
                                    onFile={(f) => uploadVideoFile(f, setUploadingFinal, setFinalVideoUrl, "final")}
                                    backend={BACKEND}
                                />
                                <VideoField
                                    label="Reel preview (ter goedkeuring door makelaar)"
                                    testid="reel-preview-video"
                                    url={reelPreviewUrl}
                                    setUrl={setReelPreviewUrl}
                                    progress={uploadingReelPreview}
                                    onFile={(f) => uploadVideoFile(f, setUploadingReelPreview, setReelPreviewUrl, "reel_preview")}
                                    backend={BACKEND}
                                />
                                <VideoField
                                    label="Reel"
                                    testid="reel-video"
                                    url={reelUrl}
                                    setUrl={setReelUrl}
                                    progress={uploadingReel}
                                    onFile={(f) => uploadVideoFile(f, setUploadingReel, setReelUrl, "reel")}
                                    backend={BACKEND}
                                />
                                <div>
                                    <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Fotoreeks</Label>
                                    <PhotoDropZone uploading={uploading} progress={photoProgress} onFiles={onUploadPhotos} />
                                    {aiPhotos.length > 0 && (
                                        <div className="mt-3 grid grid-cols-4 md:grid-cols-6 gap-2">
                                            {aiPhotos.map((u) => (
                                                <div key={u} className="relative aspect-square rounded-sm overflow-hidden border border-zinc-200">
                                                    <img src={u.startsWith("http") ? u : `${BACKEND}${u}`} alt="" className="w-full h-full object-cover" />
                                                    <button onClick={() => removeAiPhoto(u)} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/70 flex items-center justify-center"><X className="h-3 w-3" /></button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <Button onClick={saveDeliverables} disabled={savingDeliverables} className="bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98]" data-testid="save-deliverables-btn">
                                    {savingDeliverables ? "Opslaan..." : "Media opslaan"}
                                </Button>
                            </div>
                        </div>

                        {/* Comments on preview */}
                        <CommentsAdminPanel
                            comments={comments}
                            onResolve={async (cid) => {
                                await api.patch(`/requests/${id}/comments/${cid}/resolve`);
                                await load();
                            }}
                            onDelete={async (cid) => {
                                await api.delete(`/requests/${id}/comments/${cid}`);
                                await load();
                            }}
                            onReply={async (ts, text) => {
                                await api.post(`/requests/${id}/comments`, { timestamp_seconds: ts, text });
                                await load();
                            }}
                        />

                        {productsBlock}

                        <AuditLogPanel requestId={req.id} />
                    </div>

                    {/* Right — meta info */}
                    <div className="space-y-4">
                        <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-5 space-y-4">
                            <div>
                                <div className="flex items-center gap-2 mb-2">
                                    <Clock className="h-3.5 w-3.5 text-[#E5A93D]" />
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Voorkeursmomenten</div>
                                </div>
                                <div className="space-y-1.5">
                                    {[{ date: req.preferred_date, time: req.preferred_time }, ...(req.preferred_slots || [])]
                                        .filter((s) => s && s.date)
                                        .map((s, i) => (
                                            <div key={i} className="flex items-center gap-2 text-sm" data-testid={`info-pref-slot-${i}`}>
                                                <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold bg-[#E5A93D]/15 text-[#E5A93D] border border-[#E5A93D]/30">{i + 1}</span>
                                                <span className="text-zinc-950">{fmtDate(s.date)}</span>
                                                <span className="text-zinc-400">•</span>
                                                <Clock className="h-3 w-3 text-[#E5A93D]" />
                                                <span className="text-zinc-600">{s.time}</span>
                                            </div>
                                        ))}
                                    {!req.preferred_date && (!req.preferred_slots || req.preferred_slots.length === 0) && (
                                        <div className="text-sm text-zinc-500 italic">Datum nog te bepalen</div>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-start gap-3 pt-3 border-t border-zinc-100">
                                <Calendar className="h-3.5 w-3.5 text-[#E5A93D] mt-1 flex-shrink-0" />
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Gepland</div>
                                    {req.scheduled_date ? (
                                        <div className="inline-flex items-center gap-2 text-sm text-zinc-950">
                                            {fmtDate(req.scheduled_date)}
                                            {req.scheduled_time && (<><span className="text-zinc-400">•</span><Clock className="h-3 w-3 text-[#E5A93D]" /><span>{req.scheduled_time}</span></>)}
                                        </div>
                                    ) : (
                                        <div className="text-sm text-zinc-500">—</div>
                                    )}
                                </div>
                            </div>
                            {req.publication_date && (
                                <div className="flex items-start gap-3 pt-3 border-t border-zinc-100" data-testid="publication-date-row">
                                    <Calendar className="h-3.5 w-3.5 text-[#7C3AED] mt-1 flex-shrink-0" />
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#7C3AED]">Publicatiedatum pand</div>
                                        <div className="text-sm text-zinc-950">{fmtDate(req.publication_date)}</div>
                                        {(() => {
                                            const days = Math.ceil((new Date(req.publication_date) - new Date()) / (1000 * 60 * 60 * 24));
                                            if (days < 0) return <div className="text-[10px] text-zinc-500">Reeds gepubliceerd</div>;
                                            if (days === 0) return <div className="text-[10px] text-red-600 font-bold">VANDAAG</div>;
                                            return <div className="text-[10px] text-zinc-500">over {days} dag{days === 1 ? "" : "en"}</div>;
                                        })()}
                                    </div>
                                </div>
                            )}
                            <Row icon={MapPin} label="Adres" value={`${req.address_street}, ${req.address_zip} ${req.address_city}`} />
                            <Row icon={Mail} label="Email" value={req.email} />
                            <Row icon={Phone} label="GSM" value={req.phone} />
                        </div>
                    </div>
                </div>
            </div>

            <ItemsEditorModal
                open={itemsEditOpen}
                onClose={() => setItemsEditOpen(false)}
                request={req}
                products={products}
                onSaved={(updated) => setReq(updated)}
            />

            <EditRequestDetailsModal
                open={editDetailsOpen}
                onOpenChange={setEditDetailsOpen}
                req={req}
                onSaved={(updated) => setReq(updated)}
            />

            {/* Delete confirmation modal */}
            {deleteOpen && (
                <div className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !deleting && setDeleteOpen(false)} data-testid="delete-modal">
                    <div className="bg-[#FFFFFF] border border-red-500/30 rounded-md max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2 mb-3 text-red-600">
                            <Trash2 className="h-5 w-5" />
                            <h3 className="font-display font-bold text-xl">Project verwijderen?</h3>
                        </div>
                        <p className="text-sm text-zinc-600 mb-6">
                            <span className="font-bold text-zinc-950">{req.address_street}, {req.address_city}</span> wordt definitief verwijderd, samen met alle foto's, video's, opmerkingen en script.
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setDeleteOpen(false)}
                                disabled={deleting}
                                className="flex-1 h-11 border border-zinc-200 text-zinc-600 hover:bg-zinc-100 rounded-sm text-sm"
                                data-testid="cancel-delete-btn"
                            >
                                Annuleren
                            </button>
                            <button
                                onClick={async () => {
                                    setDeleting(true);
                                    try {
                                        await api.delete(`/requests/${id}`);
                                        toast.success("Project verwijderd");
                                        navigate("/admin");
                                    } catch (e) {
                                        toast.error(e.response?.data?.detail || "Verwijderen mislukt");
                                        setDeleting(false);
                                    }
                                }}
                                disabled={deleting}
                                className="flex-1 h-11 bg-red-500 hover:bg-red-400 disabled:opacity-50 text-zinc-950 font-bold rounded-sm text-sm active:scale-[0.98]"
                                data-testid="confirm-delete-btn"
                            >
                                {deleting ? "Bezig..." : "Verwijder"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ZipDownloadOverlay state={zipDl.state} onCancel={zipDl.cancel} />
            <FolderDownloadOverlay state={folderDl.state} onCancel={folderDl.cancel} />
        </div>
    );
}

function Row({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-3">
            <Icon className="h-3.5 w-3.5 text-[#E5A93D] mt-1 flex-shrink-0" />
            <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
                <div className="text-sm text-zinc-950">{value}</div>
            </div>
        </div>
    );
}

function CommentsAdminPanel({ comments, onResolve, onDelete, onReply }) {
    const open = comments.filter((c) => !c.resolved);
    const resolved = comments.filter((c) => c.resolved);
    return (
        <div className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8" data-testid="admin-comments-panel">
            <div className="flex items-center gap-2 mb-6">
                <MessageSquare className="h-4 w-4 text-[#E5A93D]" />
                <h2 className="font-display font-bold text-xl tracking-tight">Opmerkingen op preview</h2>
                {open.length > 0 && (
                    <span className="ml-auto text-xs bg-[#E5A93D] text-black font-bold px-2 py-0.5 rounded-sm">{open.length} open</span>
                )}
            </div>
            {comments.length === 0 ? (
                <div className="text-sm text-zinc-500 py-6 text-center">Nog geen opmerkingen van de makelaar</div>
            ) : (
                <div className="space-y-3">
                    {[...open, ...resolved].map((c) => (
                        <CommentAdminItem key={c.id} comment={c} onResolve={onResolve} onDelete={onDelete} onReply={onReply} />
                    ))}
                </div>
            )}
        </div>
    );
}

function formatMMSS(seconds) {
    const s = Math.floor(seconds || 0);
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

function CommentAdminItem({ comment, onResolve, onDelete, onReply }) {
    const [replying, setReplying] = React.useState(false);
    const [text, setText] = React.useState("");
    const isAdmin = comment.user_role === "admin";
    return (
        <div className={`p-4 rounded-md border ${comment.resolved ? "bg-[#FFFFFF] border-zinc-100 opacity-70" : "bg-[#F4F4F5] border-zinc-200"}`} data-testid={`admin-comment-${comment.id}`}>
            <div className="flex items-start gap-3">
                <div className="font-mono text-xs bg-[#E5A93D] text-black font-bold px-2 py-1 rounded-sm flex-shrink-0">
                    {formatMMSS(comment.timestamp_seconds)}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-medium text-zinc-950">{comment.user_name}</span>
                        {isAdmin && <span className="text-[10px] uppercase tracking-wider bg-[#E5A93D]/20 text-[#E5A93D] px-1.5 rounded-sm">Social Factory</span>}
                        <span className="text-xs text-zinc-500">{new Date(comment.created_at).toLocaleString("nl-BE")}</span>
                        {comment.resolved && <span className="text-[10px] uppercase tracking-wider bg-emerald-500/20 text-emerald-600 px-1.5 rounded-sm">Opgelost</span>}
                    </div>
                    <p className="text-sm text-zinc-800 whitespace-pre-wrap break-words">{comment.text}</p>
                    <div className="flex items-center gap-2 mt-3">
                        {!comment.resolved && (
                            <>
                                <button onClick={() => onResolve(comment.id)} className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1" data-testid={`resolve-comment-${comment.id}`}>
                                    <Check className="h-3 w-3" /> Markeer opgelost
                                </button>
                                <span className="text-zinc-400">·</span>
                                <button onClick={() => setReplying((v) => !v)} className="text-xs text-[#E5A93D] hover:underline" data-testid={`reply-comment-${comment.id}`}>
                                    {replying ? "Annuleer" : "Reageer"}
                                </button>
                            </>
                        )}
                        {!isAdmin && (
                            <>
                                <span className="text-zinc-400">·</span>
                                <button onClick={() => onDelete(comment.id)} className="text-xs text-red-600 hover:text-red-700">Verwijder</button>
                            </>
                        )}
                    </div>
                    {replying && (
                        <div className="mt-3 flex flex-col gap-2">
                            <textarea
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                className="w-full text-sm bg-[#FAFAFA] border border-zinc-200 rounded-sm p-2 text-zinc-800 focus:outline-none focus:border-[#E5A93D]"
                                rows={2}
                                placeholder="Uw antwoord..."
                                data-testid={`reply-input-${comment.id}`}
                            />
                            <button
                                onClick={() => { if (text.trim()) { onReply(comment.timestamp_seconds, text); setText(""); setReplying(false); } }}
                                className="self-end text-xs bg-[#E5A93D] text-black font-bold px-3 py-1.5 rounded-sm"
                                data-testid={`submit-reply-${comment.id}`}
                            >
                                Versturen op {formatMMSS(comment.timestamp_seconds)}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


function formatSize(bytes) {
    if (!bytes) return "";
    const mb = bytes / 1024 / 1024;
    if (mb < 1024) return `${mb.toFixed(1)} MB`;
    return `${(mb / 1024).toFixed(2)} GB`;
}

function PhotoDropZone({ uploading, progress, onFiles }) {
    const [drag, setDrag] = React.useState(false);
    return (
        <label
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDrag(true); }}
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDrag(true); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDrag(false); }}
            onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDrag(false);
                const files = Array.from(e.dataTransfer?.files || []).filter((f) => f.type.startsWith("image/"));
                if (files.length) onFiles({ target: { files } });
                else if (e.dataTransfer?.files?.length) toast.error("Sleep enkel afbeeldingen");
            }}
            className={`flex items-center justify-center gap-2 h-24 border-2 border-dashed rounded-md cursor-pointer transition-colors ${drag ? "border-[#E5A93D] bg-[#E5A93D]/5" : "border-zinc-200 hover:border-[#E5A93D]/50"}`}
            data-testid="ai-photos-upload"
        >
            <Upload className={`h-4 w-4 ${drag ? "text-[#E5A93D]" : "text-zinc-500"}`} />
            <span className={`text-sm ${drag ? "text-[#E5A93D]" : "text-zinc-500"}`}>
                {uploading
                    ? (progress?.total ? `Bezig... ${progress.done}/${progress.total} foto's` : "Bezig...")
                    : (drag ? "Laat los om te uploaden" : "Sleep foto's hier of klik")}
            </span>
            <input type="file" multiple accept="image/*" className="hidden" onChange={onFiles} />
        </label>
    );
}

function VideoField({ label, testid, url, setUrl, progress, onFile, backend }) {
    const inputId = `file-${testid}`;
    const hasFile = url && !url.startsWith("http");
    const hasExternal = url && url.startsWith("http");
    const busy = progress > 0 && progress < 100;
    const [dragOver, setDragOver] = React.useState(false);
    return (
        <div data-testid={testid}>
            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{label}</Label>

            {/* File upload area */}
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2 mb-2">
                <label
                    htmlFor={inputId}
                    onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                    onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); }}
                    onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); }}
                    onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDragOver(false);
                        const f = e.dataTransfer?.files?.[0];
                        if (f && f.type.startsWith("video/")) onFile(f);
                        else if (f) toast.error("Sleep een videobestand (mp4, mov, ...)");
                    }}
                    className={`flex items-center justify-center gap-2 h-20 border-2 border-dashed rounded-md cursor-pointer transition-colors ${busy || dragOver ? "border-[#E5A93D] bg-[#E5A93D]/5" : "border-zinc-200 hover:border-[#E5A93D]/50"}`}
                    data-testid={`${testid}-upload-area`}
                >
                    {busy ? (
                        <div className="flex flex-col items-center gap-1 w-full px-4">
                            <div className="text-xs text-[#E5A93D] font-medium">Uploaden... {progress}%</div>
                            <div className="w-full h-1 bg-zinc-100 rounded-full overflow-hidden">
                                <div className="h-full bg-[#E5A93D] transition-all" style={{ width: `${progress}%` }} />
                            </div>
                        </div>
                    ) : dragOver ? (
                        <div className="flex items-center gap-2 text-sm text-[#E5A93D]">
                            <Upload className="h-4 w-4" />
                            <span>Laat los om te uploaden</span>
                        </div>
                    ) : hasFile ? (
                        <div className="flex items-center gap-2 text-sm">
                            <span className="text-[#E5A93D]">✓ Video geüpload</span>
                            <span className="text-zinc-500">— sleep of klik om te vervangen</span>
                        </div>
                    ) : (
                        <>
                            <Upload className="h-4 w-4 text-zinc-500" />
                            <span className="text-sm text-zinc-500">Sleep video hier of klik (mp4, mov)</span>
                        </>
                    )}
                    <input
                        id={inputId}
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={(e) => onFile(e.target.files?.[0])}
                        data-testid={`${testid}-file-input`}
                    />
                </label>
                {url && (
                    <button
                        type="button"
                        onClick={() => setUrl("")}
                        className="h-20 px-4 border border-zinc-200 hover:border-zinc-400 rounded-md text-sm text-zinc-500 hover:text-zinc-950"
                        data-testid={`${testid}-clear`}
                    >
                        Wissen
                    </button>
                )}
            </div>

            {/* URL input (YouTube/Vimeo or direct) */}
            <div className="flex items-center gap-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 whitespace-nowrap">Of URL</div>
                <div className="h-px flex-1 bg-zinc-100" />
            </div>
            <Input
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="YouTube, Vimeo of directe URL..."
                className="mt-2 bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]"
                data-testid={`${testid}-url-input`}
            />

            {/* Preview */}
            {hasFile && (
                <video
                    key={url}
                    src={`${backend}${url}`}
                    controls
                    className="mt-3 w-full aspect-video bg-zinc-50 rounded-sm border border-zinc-200"
                    data-testid={`${testid}-preview`}
                />
            )}
            {hasExternal && (
                <div className="mt-2 text-xs text-zinc-500 break-all">Externe link: {url}</div>
            )}
        </div>
    );
}
